import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import api from "@/lib/api";

const COMMON_DOMAINS = ["gmail.com", "hotmail.com", "outlook.com", "yahoo.com", "yahoo.com.mx", "hotmail.com.mx", "live.com", "live.com.mx", "icloud.com", "prodigy.net.mx", "me.com"];

const levenshtein = (a, b) => {
  const m = a.length, n = b.length;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[m][n];
};

const INGRESOS = [
  { v: "facturo", t: "Facturo al SAT", d: "CFDI / Persona Moral o PFAE" },
  { v: "spei_terminal", t: "SPEI o terminal", d: "Recibo transferencias o cobro con TPV" },
  { v: "combinacion", t: "Combinación", d: "Una mezcla de las anteriores" },
  { v: "efectivo", t: "Efectivo sin facturar", d: "Principalmente efectivo" },
];
const TIEMPOS = [
  { v: "menos_1", t: "Menos de 1 año" },
  { v: "1_2", t: "1 a 2 años" },
  { v: "2_5", t: "2 a 5 años" },
  { v: "mas_5", t: "Más de 5 años" },
];
const MONTOS = [
  { v: "100k_500k", t: "$100K – $500K" },
  { v: "500k_2m", t: "$500K – $2M" },
  { v: "2m_10m", t: "$2M – $10M" },
  { v: "mas_10m", t: "Más de $10M" },
];
const DESTINOS = [
  "Capital de trabajo / inventario",
  "Compra o renovación de activos / equipo",
  "Expansión / nuevas operaciones",
  "Oportunidad de negocio específica",
  "Consolidar deudas",
];
const BUROS = [
  { v: "al_corriente", t: "Sin atrasos, al corriente" },
  { v: "atraso_menor", t: "Algún atraso menor ya resuelto" },
  { v: "vencidas_activas", t: "Tengo cuentas vencidas activas" },
  { v: "no_se", t: "No lo sé / no he revisado" },
];
const URGENCIAS = [
  { v: "urgente", t: "Urgente (esta semana)" },
  { v: "mes", t: "En el próximo mes" },
  { v: "evaluando", t: "Estoy evaluando opciones" },
];

const TOTAL = 5;

const Card = ({ active, onClick, title, desc, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`w-full text-left p-5 border transition-all duration-200 ${
      active ? "border-[#04b0ba] bg-[#04b0ba]/5 ring-1 ring-[#04b0ba]" : "border-[#E4E4E7] hover:border-[#333333] bg-white"
    }`}
  >
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="font-semibold text-[#333333]">{title}</div>
        {desc && <div className="text-sm text-[#6B6B6B] mt-0.5">{desc}</div>}
      </div>
      <div className={`shrink-0 w-5 h-5 rounded-full border grid place-items-center ${active ? "bg-[#04b0ba] border-[#04b0ba]" : "border-[#c9c9cf]"}`}>
        {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
      </div>
    </div>
  </button>
);

const Field = ({ k, label, placeholder, hint, value, error, onChange, onlyDigits = false, maxLength }) => (
  <div>
    <label className="label-caps block mb-1.5">{label}</label>
    <input
      data-testid={`quiz-input-${k}`}
      value={value}
      inputMode={onlyDigits ? "numeric" : undefined}
      maxLength={maxLength}
      onChange={(e) => onChange(k, onlyDigits ? e.target.value.replace(/\D/g, "").slice(0, maxLength || 999) : e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-3 border bg-white outline-none focus:border-[#04b0ba] transition-colors ${error ? "border-red-500" : "border-[#E4E4E7]"}`}
    />
    {hint && !error && <p className="text-xs text-[#6B6B6B] mt-1">{hint}</p>}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

export default function PrequalQuiz({ onComplete }) {
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    tipo_ingreso: "", tiempo_operando: "", monto: "", destinos: [],
    historial_buro: "", garantia: "", nombre: "", empresa: "",
    whatsapp: "", email: "", rfc: "", urgencia: "",
  });
  const [errors, setErrors] = useState({});
  const rootRef = useRef(null);
  const [emailStatus, setEmailStatus] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailSuggestion, setEmailSuggestion] = useState("");

  const checkEmail = async (value) => {
    const email = (value || "").trim();
    setEmailSuggestion("");
    if (!email) { setEmailStatus(""); setEmailMsg(""); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setEmailStatus("invalid"); setEmailMsg("Correo no verificado — formato inválido."); return;
    }
    const [local, domain] = email.split("@");
    const dom = domain.toLowerCase();
    // Detección de errores de dedo: si el dominio se parece mucho a uno común
    // pero no es exactamente igual, se marca como NO verificado y se sugiere.
    if (!COMMON_DOMAINS.includes(dom)) {
      let best = null, bestD = 99;
      for (const d of COMMON_DOMAINS) { const dist = levenshtein(dom, d); if (dist < bestD) { bestD = dist; best = d; } }
      if (best && bestD > 0 && bestD <= 2) {
        setEmailSuggestion(`${local}@${best}`);
        setEmailStatus("invalid");
        setEmailMsg("Correo no verificado — el dominio parece estar mal escrito.");
        return;
      }
    }
    setEmailStatus("checking"); setEmailMsg("");
    try {
      const { data } = await api.post("/validate-email", { email });
      if (data.valid) { setEmailStatus("valid"); setEmailMsg(""); }
      else {
        setEmailStatus("invalid");
        setEmailMsg(data.reason === "formato"
          ? "Correo no verificado — formato inválido."
          : "Correo no verificado — el dominio no existe o no puede recibir correos.");
      }
    } catch (e) {
      setEmailStatus(""); setEmailMsg("");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDestino = (d) =>
    setForm((f) => ({ ...f, destinos: f.destinos.includes(d) ? f.destinos.filter((x) => x !== d) : [...f.destinos, d] }));

  const go = (next) => {
    setDir(next > step ? 1 : -1);
    setStep(next);
    setErrors({});
    requestAnimationFrame(() =>
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60)
    );
  };

  const validateStep = () => {
    const e = {};
    if (step === 1 && !form.tipo_ingreso) e.tipo_ingreso = "Selecciona una opción";
    if (step === 2 && !form.tiempo_operando) e.tiempo_operando = "Selecciona una opción";
    if (step === 3) { if (!form.monto) e.monto = "Selecciona un monto"; if (form.destinos.length === 0) e.destinos = "Elige al menos un destino"; }
    if (step === 4) { if (!form.historial_buro) e.historial_buro = "Selecciona una opción"; }
    if (step === 5) {
      if (!form.nombre.trim()) e.nombre = "Requerido";
      if (!form.empresa.trim()) e.empresa = "Requerido";
      if (!/^\d{10}$/.test(form.whatsapp.replace(/\D/g, ""))) e.whatsapp = "WhatsApp a 10 dígitos";
      if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Correo inválido";
      else if (emailStatus === "invalid") e.email = emailMsg || "Correo inválido";
      if (!form.urgencia) e.urgencia = "Selecciona una opción";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) go(step + 1); };

  const submit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const payload = { ...form, whatsapp: form.whatsapp.replace(/\D/g, "") };
      const { data } = await api.post("/leads", payload);
      onComplete(data);
    } catch (err) {
      setErrors({ submit: "Ocurrió un error al enviar. Intenta de nuevo." });
    } finally {
      setLoading(false);
    }
  };

  const variants = {
    enter: (d) => ({ x: d > 0 ? 40 : -40, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -40 : 40, opacity: 0 }),
  };

  return (
    <div ref={rootRef} className="border border-[#E4E4E7] bg-white scroll-mt-24" data-testid="prequal-quiz">
      {/* progress */}
      <div className="px-6 sm:px-8 pt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="label-caps">Paso {step} de {TOTAL}</span>
          <span className="text-xs font-semibold text-[#04b0ba]">{Math.round((step / TOTAL) * 100)}%</span>
        </div>
        <div className="h-1 bg-[#E4E4E7]">
          <motion.div className="h-full bg-[#04b0ba]" animate={{ width: `${(step / TOTAL) * 100}%` }} transition={{ duration: 0.4 }} />
        </div>
      </div>

      <div className="p-6 sm:p-8 overflow-hidden">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div key={step} custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.28 }}>
            {step === 1 && (
              <div>
                <h3 className="font-display text-2xl font-semibold mb-1">¿Cómo genera ingresos tu negocio?</h3>
                <p className="text-sm text-[#6B6B6B] mb-5">Esto nos ayuda a saber qué instituciones pueden atenderte.</p>
                <div className="grid gap-3">
                  {INGRESOS.map((o) => <Card key={o.v} testid={`ingreso-${o.v}`} active={form.tipo_ingreso === o.v} onClick={() => set("tipo_ingreso", o.v)} title={o.t} desc={o.d} />)}
                </div>
                {errors.tipo_ingreso && <p className="text-xs text-red-500 mt-2">{errors.tipo_ingreso}</p>}
              </div>
            )}
            {step === 2 && (
              <div>
                <h3 className="font-display text-2xl font-semibold mb-1">¿Cuánto tiempo lleva operando formalmente?</h3>
                <p className="text-sm text-[#6B6B6B] mb-5">La mayoría de instituciones requieren un mínimo de operación.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {TIEMPOS.map((o) => <Card key={o.v} testid={`tiempo-${o.v}`} active={form.tiempo_operando === o.v} onClick={() => set("tiempo_operando", o.v)} title={o.t} />)}
                </div>
                {errors.tiempo_operando && <p className="text-xs text-red-500 mt-2">{errors.tiempo_operando}</p>}
              </div>
            )}
            {step === 3 && (
              <div>
                <h3 className="font-display text-2xl font-semibold mb-1">¿Cuánto capital necesitas y para qué?</h3>
                <p className="text-sm text-[#6B6B6B] mb-5">El monto y destino determinan el producto más adecuado.</p>
                <label className="label-caps block mb-2">Monto aproximado</label>
                <div className="grid sm:grid-cols-2 gap-3 mb-2">
                  {MONTOS.map((o) => <Card key={o.v} testid={`monto-${o.v}`} active={form.monto === o.v} onClick={() => set("monto", o.v)} title={o.t} />)}
                </div>
                {errors.monto && <p className="text-xs text-red-500 mb-2">{errors.monto}</p>}
                <label className="label-caps block mb-2 mt-4">Destino del crédito (elige los que apliquen)</label>
                <div className="flex flex-wrap gap-2">
                  {DESTINOS.map((d) => (
                    <button key={d} type="button" data-testid={`destino-${d.slice(0, 8)}`} onClick={() => toggleDestino(d)}
                      className={`px-3 py-2 text-sm border transition-colors ${form.destinos.includes(d) ? "border-[#04b0ba] bg-[#04b0ba] text-white" : "border-[#E4E4E7] hover:border-[#333333]"}`}>
                      {d}
                    </button>
                  ))}
                </div>
                {errors.destinos && <p className="text-xs text-red-500 mt-2">{errors.destinos}</p>}
              </div>
            )}
            {step === 4 && (
              <div>
                <h3 className="font-display text-2xl font-semibold mb-1">¿Cómo está tu historial en Buró?</h3>
                <p className="text-sm text-[#6B6B6B] mb-5">Esto determina tus condiciones, no si podemos ayudarte.</p>
                <div className="grid gap-3">
                  {BUROS.map((o) => <Card key={o.v} testid={`buro-${o.v}`} active={form.historial_buro === o.v} onClick={() => set("historial_buro", o.v)} title={o.t} />)}
                </div>
                {errors.historial_buro && <p className="text-xs text-red-500 mt-2">{errors.historial_buro}</p>}
                <div className="mt-5">
                  <label className="label-caps block mb-2">¿Cuentas con una garantía hipotecaria (propiedad)?</label>
                  <div className="grid grid-cols-2 gap-3">
                    <Card testid="garantia-si" active={form.garantia === "si"} onClick={() => set("garantia", "si")} title="Sí, tengo propiedad" />
                    <Card testid="garantia-no" active={form.garantia === "no"} onClick={() => set("garantia", "no")} title="No cuento con una" />
                  </div>
                </div>
              </div>
            )}
            {step === 5 && (
              <div>
                <h3 className="font-display text-2xl font-semibold mb-1">Ya tenemos tu perfil preliminar</h3>
                <p className="text-sm text-[#6B6B6B] mb-5">¿A quién le enviamos la evaluación?</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field k="nombre" label="Nombre completo" placeholder="Tu nombre" value={form.nombre} error={errors.nombre} onChange={set} />
                  <Field k="empresa" label="Empresa" placeholder="Nombre de tu empresa" value={form.empresa} error={errors.empresa} onChange={set} />
                  <Field k="whatsapp" label="WhatsApp (10 dígitos)" placeholder="3312345678" value={form.whatsapp} error={errors.whatsapp} onChange={set} onlyDigits maxLength={10} />
                  <div>
                    <label className="label-caps block mb-1.5">Correo electrónico</label>
                    <div className="relative">
                      <input
                        data-testid="quiz-input-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => { set("email", e.target.value); setErrors((prev) => (prev.email ? { ...prev, email: undefined } : prev)); if (emailStatus) { setEmailStatus(""); setEmailMsg(""); setEmailSuggestion(""); } }}
                        onBlur={(e) => checkEmail(e.target.value)}
                        placeholder="tu@empresa.com"
                        className={`w-full px-4 py-3 pr-10 border bg-white outline-none focus:border-[#04b0ba] transition-colors ${errors.email || emailStatus === "invalid" ? "border-red-500" : emailStatus === "valid" ? "border-[#04b0ba]" : "border-[#E4E4E7]"}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2">
                        {emailStatus === "checking" && <Loader2 data-testid="email-checking" className="w-4 h-4 animate-spin text-[#52525B]" />}
                        {emailStatus === "valid" && <Check data-testid="email-valid" className="w-4 h-4 text-[#04b0ba]" strokeWidth={3} />}
                        {emailStatus === "invalid" && <AlertCircle data-testid="email-invalid" className="w-4 h-4 text-red-500" />}
                      </span>
                    </div>
                    {emailSuggestion && (
                      <button type="button" data-testid="email-suggestion" onClick={() => { set("email", emailSuggestion); checkEmail(emailSuggestion); }} className="text-xs text-[#04b0ba] mt-1 hover:underline font-medium">
                        ¿Quisiste decir {emailSuggestion}?
                      </button>
                    )}
                    {(errors.email || (emailStatus === "invalid" && emailMsg)) && <p className="text-xs text-red-500 mt-1">{errors.email || emailMsg}</p>}
                    {emailStatus === "valid" && !errors.email && <p className="text-xs text-[#04b0ba] mt-1">Correo verificado.</p>}
                  </div>
                  <Field k="rfc" label="RFC (opcional)" placeholder="Agiliza tu evaluación 2x" hint="Nos permite agilizar tu evaluación." value={form.rfc} error={errors.rfc} onChange={set} />
                </div>
                <div className="mt-5">
                  <label className="label-caps block mb-2">¿Qué tan urgente lo necesitas?</label>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {URGENCIAS.map((o) => <Card key={o.v} testid={`urgencia-${o.v}`} active={form.urgencia === o.v} onClick={() => set("urgencia", o.v)} title={o.t} />)}
                  </div>
                  {errors.urgencia && <p className="text-xs text-red-500 mt-2">{errors.urgencia}</p>}
                </div>
                {errors.submit && <p className="text-sm text-red-500 mt-3">{errors.submit}</p>}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* nav */}
        <div className="flex items-center justify-between gap-4 mt-8">
          {step > 1 ? (
            <button data-testid="quiz-back-button" onClick={() => go(step - 1)} className="inline-flex items-center gap-2 text-sm font-semibold text-[#6B6B6B] hover:text-[#333333]">
              <ArrowLeft className="w-4 h-4" /> Anterior
            </button>
          ) : <span className="inline-flex items-center gap-1.5 text-xs text-[#6B6B6B]"><ShieldCheck className="w-4 h-4 text-[#04b0ba]" /> Tus datos están protegidos</span>}

          {step < TOTAL ? (
            <button data-testid="quiz-next-button" onClick={next} className="inline-flex items-center gap-2 bg-[#04b0ba] hover:bg-[#038f98] text-white font-semibold px-6 py-3 transition-colors">
              Continuar <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="quiz-submit-button" onClick={submit} disabled={loading} className="inline-flex items-center gap-2 bg-[#f05c3e] hover:brightness-95 text-white font-semibold px-6 py-3 transition-all disabled:opacity-60">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Evaluando…</> : <>Ver mi resultado <ArrowRight className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
